import { Component, OnInit } from '@angular/core';
import { AuthUtilsService } from '@app/auth/services/auth-utils.service';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { OIDCService } from '@app/auth/services/oidc.service';
import { SecurityRoleLevelToRoleMapping } from '@app/core/interfaces/security-role-level';
import { AuthUser } from '@app/core/models/auth-user';
import { UserLevel } from '@app/core/models/user-level';
import { MenuService } from '@app/menu/services/menu.service';
import { RouterService } from '@app/routing/router.service';
import { EndUserContextService } from '@app/shared/services/context/end-user-context.service';
import { PreferenceStorageService } from '@app/shared/services/preference-storage.service';
import { PortalTranslateService } from '@app/translate/services/portal-translate.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

@UntilDestroy()
@Component({
  selector: 'portal-header',
  templateUrl: './portal-header.component.html',
  standalone: false,
})
export class PortalHeaderComponent implements OnInit {
  user$: Observable<AuthUser>;

  SecurityRoleLevelToRoleMapping = SecurityRoleLevelToRoleMapping;

  /**
   * The currently active language
   */
  activeLanguage: string;
  authUserIsEndUser: boolean = false;

  /**
   * Used to toggle the display of the translator
   */
  enableTranslator: boolean = environment.enableTranslator === true;
  languages: string[] = [];
  richPresenceActive: boolean = false;
  userAccountURL: string | null = null;

  protected readonly UserLevel = UserLevel;

  constructor(
    private authenticationService: AuthenticationService,
    private authUtilsService: AuthUtilsService,
    private endUserContextService: EndUserContextService,
    private menuService: MenuService,
    private portalTranslateService: PortalTranslateService,
    private preferenceStorageService: PreferenceStorageService,
    private routerService: RouterService,
    private translateService: TranslateService,
    private oidcService: OIDCService
  ) {
    this.user$ = this.authenticationService.authUser$.pipe(untilDestroyed(this));
    this.authUserIsEndUser = this.authenticationService.userIsEndUser();
  }

  ngOnInit() {
    // this.userAccountURL = this.oidcService.userAccountURL();
    this.preferenceStorageService
      .preference$<string>('preferredLanguage', 'de')
      .subscribe((preferredLanguage) => {
        this.activeLanguage = preferredLanguage.toLowerCase();
      });

    this.portalTranslateService.languageKeys$.subscribe((languages) => {
      this.languages = languages;
    });

    this.translateService.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.activeLanguage = event.lang.toLowerCase();
    });

    this.authenticationService.authUser$.pipe(untilDestroyed(this)).subscribe((user) => {
      this.authUserIsEndUser = user.role.level === UserLevel.EndUser;

      if (this.authUserIsEndUser) {
        this.endUserContextService
          .canUseRichPresence$()
          .pipe(untilDestroyed(this))
          .subscribe((canUseRichPresence) => {
            this.richPresenceActive = canUseRichPresence;
          });
      }
    });
  }

  loadTranslator() {
    this.portalTranslateService.runTranslator();
  }

  logout() {
    this.authenticationService.logout().subscribe(() => {
      this.authUtilsService.redirectToLogin();
    });
  }

  openTranslationOverview() {
    this.routerService.navigateByUrl('/translations');
  }

  switchLanguage(lang: string) {
    this.portalTranslateService.use(lang);
  }

  toggleSidebar() {
    this.menuService.toggleSidebar();
  }
}
